import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Insights() {
  const navigate = useNavigate();
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);

  // ============================================================
  // Validation Dashboard Data
  // ============================================================
  const validationData = {
    modelVersion: 'CTF v1.0',
    validationStatus: 'Retrospective Validation',
    dataset: '500 Patients',
    disease: 'Tuberculosis',
    sensitivity: '96.1%',
    specificity: '92.8%',
    precision: '91.4%',
    recall: '96.1%',
    auc: '0.95',
    calibration: 'Excellent',
    lastUpdated: 'July 2026'
  };

  // ============================================================
  // Disease Areas
  // ============================================================
  const diseaseAreas = [
    { icon: '🫁', label: 'Tuberculosis', route: '/insights/tuberculosis' },
    { icon: '🌬️', label: 'COPD', route: '/insights/copd' },
    { icon: '💨', label: 'ILD', route: '/insights/ild' },
    { icon: '❤️', label: 'Pneumonia', route: '/insights/pneumonia' },
    { icon: '🎯', label: 'Lung Cancer', route: '/insights/lung-cancer' }
  ];

  // ============================================================
  // Research
  // ============================================================
  const researchItems = [
    {
      icon: '📄',
      label: 'Publications',
      subtitle: 'Peer-reviewed research',
      desc: 'Peer-reviewed research manuscripts and global case studies.',
      route: '/insights/publications'
    },
    {
      icon: '✅',
      label: 'Validation Studies',
      subtitle: 'Clinical performance data',
      desc: 'Clinical trial performance metrics and dataset benchmarks.',
      route: '/insights/validation-studies'
    },
    {
      icon: '🔍',
      label: 'Biomarker Discovery',
      subtitle: 'Digital biomarkers',
      desc: 'Identification models across multi-omic & acoustic spaces.',
      route: '/insights/biomarker-discovery'
    }
  ];

  // ============================================================
  // Innovation
  // ============================================================
  const innovationItems = [
    {
      icon: '💡',
      label: 'Patents',
      subtitle: 'Intellectual property',
      desc: 'Novel AI-driven diagnostic methodologies and systems.',
      route: '/insights/patents'
    },
    {
      icon: '🚀',
      label: 'Latest Innovation',
      subtitle: 'Cutting-edge developments',
      desc: 'Recent breakthroughs and emerging technologies in AI-driven diagnostics.',
      route: '/insights/latest-innovation'
    }
  ];

  // Stats from ClinicalHub
  const stats = [
    { label: 'Models Deployed', value: '12+', icon: '🧠' },
    { label: 'Clinical Trials', value: '8', icon: '🔬' },
    { label: 'Accuracy Rate', value: '94%', icon: '✅' },
    { label: 'Data Sources', value: '3', icon: '📊' },
  ];

  // Quick Actions from ClinicalHub
  const quickActions = [
    { label: 'Run CTF Fusion', icon: '🔬', path: '/platform/ctf', variant: 'primary' },
    { label: 'Explore XAI', icon: '🧠', path: '/diagnostic-view', variant: 'outline' },
    { label: 'View Analytics', icon: '📊', path: '/analytics', variant: 'outline' },
  ];

  // ============================================================
  // Validation Roadmap
  // ============================================================
  const validationRoadmapItems = [
    {
      icon: '🏗️',
      label: 'Architecture',
      desc: '4-modality CTF fusion core implemented'
    },
    {
      icon: '📋',
      label: 'Validation Study',
      desc: 'Retrospective study design in planning'
    },
    {
      icon: '⚖️',
      label: 'Regulatory Pathway',
      desc: 'CDSCO submission preparation underway'
    },
    {
      icon: '📜',
      label: 'IP Protection',
      desc: 'Granted India patent · U.S. applications pending'
    }
  ];

  // Split disease areas into first 3 and next 2
  const diseaseFirstRow = diseaseAreas.slice(0, 3);
  const diseaseSecondRow = diseaseAreas.slice(3, 5);

  // Shared card classes
  const cardClass =
    "flex-1 min-w-[250px] group relative bg-white border border-rule rounded-xl p-5 shadow-[4px_4px_0_0_rgba(20,184,166,0.18)] hover:shadow-[6px_6px_0_0_rgba(20,184,166,0.3)] hover:-translate-y-0.5 hover:-translate-x-0.5 transition-all duration-200";
  const iconTileClass =
    "w-12 h-12 rounded-lg bg-tealLight border border-teal/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform text-2xl";
  const sectionBadgeClass =
    "inline-flex items-center gap-1.5 bg-tealLight text-teal text-[10px] font-bold tracking-wider uppercase px-3 py-1.5 rounded-md border border-teal/20";
  const sectionIconChipClass =
    "w-9 h-9 rounded-lg bg-tealLight border border-teal/20 flex items-center justify-center text-lg";
  const statCardClass =
    "flex-1 min-w-[120px] group bg-white border border-rule rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-all duration-200";
  const metricCardClass =
    "flex-1 min-w-[80px] bg-tealLight/30 border border-teal/10 rounded-lg p-3 text-center hover:bg-tealLight/50 transition-all duration-200";
  const infoBadgeClass =
    "flex-1 min-w-[100px] bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 text-center hover:bg-indigo-100 transition-colors";

  return (
    <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper text-ink antialiased selection:bg-teal/20 selection:text-ink relative">
      {/* ════════════════════════════════════════════════════════════════ */}
      {/* DISCLAIMER — Top Left Corner (Collapsible) */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={() => setIsDisclaimerOpen(!isDisclaimerOpen)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50/95 border border-amber-200 rounded-lg shadow-lg hover:bg-amber-100 transition-colors text-xs"
        >
          <span>⚠️</span>
          <span className="font-medium text-amber-800">Disclaimer</span>
          <span className="text-amber-600 text-[10px]">
            {isDisclaimerOpen ? '▲' : '▼'}
          </span>
        </button>
        
        {isDisclaimerOpen && (
          <div className="mt-2 p-3 bg-amber-50/95 border-l-4 border-amber-500 rounded-r-lg shadow-lg max-w-xs">
            <div className="flex items-start gap-2">
              <span className="text-lg flex-shrink-0 mt-0.5">⚠️</span>
              <div className="text-xs text-amber-800/90 leading-relaxed">
                <p className="font-semibold text-amber-900 text-[10px] uppercase tracking-wider">Development & Disclaimer Notice</p>
                <ul className="mt-1 space-y-0.5 list-disc list-inside text-[10px] text-amber-800/80">
                  <li>This website is under active development.</li>
                  <li>Metrics and values may be simulated, illustrative, provisional, or test data.</li>
                  <li>This content is not a validated clinical or commercial claim.</li>
                  <li>This platform is not for diagnosis, treatment, patient care, or emergency use.</li>
                  <li>This platform is not currently a commercial medical product.</li>
                  <li>Regulatory review and prospective clinical validation are not yet complete.</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 font-sans">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tealLight border border-teal/20 text-teal text-xs font-semibold uppercase tracking-wider">
              Knowledge Hub
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-bold leading-tight tracking-tight text-center text-teal">
            Platform Insights
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-inkSoft max-w-3xl mx-auto leading-relaxed text-center">
            A centralized hub for exploring underlying technology architectures,
            active pulmonary clinical applications, and rigorous evidence-based outcomes.
          </p>
        </div>

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* VALIDATION DASHBOARD */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <div className="bg-white border border-teal/20 rounded-2xl overflow-hidden mb-16 shadow-lg shadow-teal/5">
          {/* RIBBON HEADER - Compact */}
          <div className="bg-gradient-to-r from-teal via-teal/90 to-tealDark px-6 md:px-8 py-3 md:py-3.5">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3">
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-white tracking-tight">
                  CTF Validation Dashboard
                </h2>
                <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-[9px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full backdrop-blur-sm border border-white/10">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse"></span>
                  Live
                </span>
              </div>
              <p className="text-tealLight/80 text-[11px] font-medium mt-0.5">
                Model performance metrics from retrospective validation study
              </p>
            </div>
          </div>

          {/* Dashboard Body - Compact */}
          <div className="p-4 md:p-5">
            {/* Info Badges - Full Row with Contrast Color (Indigo) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
              <div className={infoBadgeClass}>
                <span className="text-indigo-600/70 text-[10px] uppercase tracking-wider font-semibold">Model</span>
                <p className="font-bold text-indigo-900 text-sm mt-0.5">{validationData.modelVersion}</p>
              </div>
              <div className={infoBadgeClass}>
                <span className="text-indigo-600/70 text-[10px] uppercase tracking-wider font-semibold">Status</span>
                <p className="font-bold text-indigo-900 text-sm mt-0.5">{validationData.validationStatus}</p>
              </div>
              <div className={infoBadgeClass}>
                <span className="text-indigo-600/70 text-[10px] uppercase tracking-wider font-semibold">Dataset</span>
                <p className="font-bold text-indigo-900 text-sm mt-0.5">{validationData.dataset}</p>
              </div>
              <div className={infoBadgeClass}>
                <span className="text-indigo-600/70 text-[10px] uppercase tracking-wider font-semibold">Disease</span>
                <p className="font-bold text-indigo-900 text-sm mt-0.5">{validationData.disease}</p>
              </div>
            </div>

            {/* Metrics Grid - Compact */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5 mb-3">
              <div className={metricCardClass}>
                <p className="text-xl font-bold text-teal">{validationData.sensitivity}</p>
                <p className="text-[9px] uppercase tracking-wider text-inkSoft font-semibold">Sensitivity</p>
              </div>
              <div className={metricCardClass}>
                <p className="text-xl font-bold text-teal">{validationData.specificity}</p>
                <p className="text-[9px] uppercase tracking-wider text-inkSoft font-semibold">Specificity</p>
              </div>
              <div className={metricCardClass}>
                <p className="text-xl font-bold text-teal">{validationData.precision}</p>
                <p className="text-[9px] uppercase tracking-wider text-inkSoft font-semibold">Precision</p>
              </div>
              <div className={metricCardClass}>
                <p className="text-xl font-bold text-teal">{validationData.recall}</p>
                <p className="text-[9px] uppercase tracking-wider text-inkSoft font-semibold">Recall</p>
              </div>
              <div className={metricCardClass}>
                <p className="text-xl font-bold text-teal">{validationData.auc}</p>
                <p className="text-[9px] uppercase tracking-wider text-inkSoft font-semibold">AUC</p>
              </div>
              <div className={metricCardClass}>
                <p className="text-xl font-bold text-teal">{validationData.calibration}</p>
                <p className="text-[9px] uppercase tracking-wider text-inkSoft font-semibold">Calibration</p>
              </div>
            </div>

            {/* Footer - Compact */}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-teal/10">
              <div className="flex items-center gap-3 text-[11px] text-inkSoft">
                <span>📅 Last Updated: <span className="font-medium text-ink">{validationData.lastUpdated}</span></span>
                <span className="w-px h-3 bg-teal/20"></span>
                <span>🔬 Retrospective Cohort</span>
                <span className="w-px h-3 bg-teal/20"></span>
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                  <span className="text-green-600 font-medium text-[10px]">Verified</span>
                </span>
              </div>
              <Link
                to="/insights/validation-studies"
                className="text-teal hover:text-teal/80 text-[11px] font-semibold flex items-center gap-1 transition-colors"
              >
                View Full Report →
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="flex flex-wrap gap-4 mb-12 justify-center">
          {stats.map((stat, index) => (
            <div key={index} className={statCardClass}>
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-2xl">{stat.icon}</span>
                <span className="text-2xl font-bold text-ink">{stat.value}</span>
              </div>
              <p className="text-sm text-inkSoft">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Validation Roadmap Card */}
        <div className="bg-white border border-rule rounded-xl p-6 mb-16 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <span className={sectionIconChipClass}>🧪</span>
            <h2 className="text-2xl font-bold text-ink tracking-tight">
              Validation Roadmap
            </h2>
            <span className={sectionBadgeClass}>
              <span className="w-1.5 h-1.5 rounded-full bg-teal"></span>
              In Progress
            </span>
          </div>
          <p className="text-inkSoft text-sm mb-6 font-medium">
            Our path from architecture to clinically validated diagnostic evidence — updated as milestones are reached.
          </p>
          <div className="flex flex-wrap gap-4">
            {validationRoadmapItems.map((item, index) => (
              <div key={index} className={statCardClass}>
                <div className="text-2xl mb-1">{item.icon}</div>
                <p className="text-sm font-bold text-ink">{item.label}</p>
                <p className="text-xs text-inkSoft mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-inkSoft text-xs mt-6 italic">
            Clinical performance metrics (sensitivity, specificity, AUC) will be published here once a validation study on real patient data is completed and peer-reviewed. No performance claims are made prior to that milestone.
          </p>
        </div>

        {/* Section Divider */}
        <hr className="border-rule mb-16" />

        {/* Section: Disease Areas */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <span className={sectionIconChipClass}>🩺</span>
              <h2 className="text-2xl font-bold text-ink tracking-tight">
                Disease Areas
              </h2>
            </div>
            <span className={sectionBadgeClass}>
              <span className="w-1.5 h-1.5 rounded-full bg-teal"></span>
              Clinical Scope
            </span>
          </div>
          <p className="text-inkSoft text-sm mb-8 font-medium">
            Target pulmonary environments optimized for precise clinical discovery.
          </p>
          <div className="flex flex-wrap gap-4 mb-4">
            {diseaseFirstRow.map((item, index) => (
              <Link key={index} to={item.route} className={cardClass}>
                <div className="flex items-start gap-4">
                  <div className={iconTileClass}>
                    <span className="text-3xl">{item.icon}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-ink group-hover:text-teal transition-colors">
                      {item.label}
                    </h3>
                  </div>
                </div>
                <div className="mt-3 text-right">
                  <span className="text-teal/50 group-hover:text-teal group-hover:translate-x-1 transition-all text-sm">→</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="flex flex-wrap gap-4">
            {diseaseSecondRow.map((item, index) => (
              <Link key={index} to={item.route} className={cardClass}>
                <div className="flex items-start gap-4">
                  <div className={iconTileClass}>
                    <span className="text-3xl">{item.icon}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-ink group-hover:text-teal transition-colors">
                      {item.label}
                    </h3>
                  </div>
                </div>
                <div className="mt-3 text-right">
                  <span className="text-teal/50 group-hover:text-teal group-hover:translate-x-1 transition-all text-sm">→</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Section Divider */}
        <hr className="border-rule mb-16" />

        {/* Section: Research */}
        <div className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <span className={sectionIconChipClass}>📊</span>
              <h2 className="text-2xl font-bold text-ink tracking-tight">
                Research
              </h2>
            </div>
            <span className={sectionBadgeClass}>
              <span className="w-1.5 h-1.5 rounded-full bg-teal"></span>
              Scientific Verification
            </span>
          </div>
          <p className="text-inkSoft text-sm mb-8 font-medium">
            Rigorous scientific research and validation studies.
          </p>
          <div className="flex flex-wrap gap-4">
            {researchItems.map((item, index) => (
              <Link key={index} to={item.route} className={cardClass}>
                <div className="flex items-start gap-4">
                  <div className={iconTileClass}>
                    <span className="text-3xl">{item.icon}</span>
                  </div>
                  <div className="flex-1">
                    <div>
                      <h3 className="text-base font-bold text-ink group-hover:text-teal transition-colors mb-0.5">
                        {item.label}
                      </h3>
                      <p className="text-xs font-medium text-teal/70 mb-1.5">
                        {item.subtitle}
                      </p>
                    </div>
                    <p className="text-inkSoft text-xs leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
                <div className="mt-3 text-right">
                  <span className="text-teal/50 group-hover:text-teal group-hover:translate-x-1 transition-all text-sm">→</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Section Divider */}
        <hr className="border-rule mb-16" />

        {/* Section: Innovation */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <span className={sectionIconChipClass}>💡</span>
              <h2 className="text-2xl font-bold text-ink tracking-tight">
                Innovation
              </h2>
            </div>
            <span className={sectionBadgeClass}>
              <span className="w-1.5 h-1.5 rounded-full bg-teal"></span>
              Cutting-Edge
            </span>
          </div>
          <p className="text-inkSoft text-sm mb-8 font-medium">
            Intellectual property and latest breakthrough innovations.
          </p>
          <div className="flex flex-wrap gap-4">
            {innovationItems.map((item, index) => (
              <Link key={index} to={item.route} className={cardClass}>
                <div className="flex items-start gap-4">
                  <div className={iconTileClass}>
                    <span className="text-3xl">{item.icon}</span>
                  </div>
                  <div className="flex-1">
                    <div>
                      <h3 className="text-base font-bold text-ink group-hover:text-teal transition-colors mb-0.5">
                        {item.label}
                      </h3>
                      <p className="text-xs font-medium text-teal/70 mb-1.5">
                        {item.subtitle}
                      </p>
                    </div>
                    <p className="text-inkSoft text-xs leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
                <div className="mt-3 text-right">
                  <span className="text-teal/50 group-hover:text-teal group-hover:translate-x-1 transition-all text-sm">→</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-12 p-6 bg-white border border-rule rounded-xl shadow-sm">
          <h2 className="text-xl font-bold text-ink tracking-tight mb-4 flex items-center gap-2">
            ⚡ Quick Actions
          </h2>
          <div className="flex flex-wrap gap-4">
            {quickActions.map((action, index) => {
              const isPrimary = action.variant === 'primary';
              return (
                <button
                  key={index}
                  onClick={() => navigate(action.path)}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
                    isPrimary
                      ? 'bg-teal text-white hover:bg-teal/90 shadow-md hover:shadow-lg'
                      : 'bg-tealLight text-teal border border-teal/30 hover:bg-teal/10 hover:border-teal/50'
                  }`}
                >
                  <span>{action.icon}</span>
                  {action.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-20 p-6 bg-white border border-rule rounded-xl text-center shadow-sm">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-teal text-sm">📌</span>
            <span className="text-xs font-semibold tracking-wide uppercase text-teal">
              Continuous Data Integrity
            </span>
          </div>
          <p className="text-inkSoft text-sm leading-relaxed">
            All analytical pipelines, verification registries, and platform insights are constantly updated
            in alignment with emerging peer-reviewed discoveries and clinical validation studies.
          </p>
        </div>
      </div>
    </div>
  );
}
